import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useRef, useState } from 'react';

import { Icon, IconKebab, IconNewTab, IconSwitch } from '@flixbus/honeycomb-icons-react';
import { Button, Spinner, Tooltip, useSmartPosition } from '@flixbus/honeycomb-react';

import { SIDEBAR_NAMES } from 'common/activeElements/sidebars/sidebarsNames';
import GridSticky from 'common/GridSticky';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';
import { getStationsForAssignment } from 'utils/assigmentsGenerator';
import { getPlanningUrl } from 'utils/getRunPlanningUrl';

import AssignmentActionMenu from './AssigmentActionMenu';
import AssigmentDuration from './AssignmentDuration';
import AssignmentStatusTag from './AssignmentStatusTag';
import { ASSIGNMENT_STATUSES } from './const';
import { ASSIGNMENT_LABEL_TEXT } from './text';

import styles from './styles.module.css';

const MultiRideIndicator = ({ storeId }) => {
	const [isTooltipActive, setIsTooltipActive] = useState(false);
	const [smartPosition, smartAlignment, targetInnerRef, balloonInnerRef] = useSmartPosition({
		active: isTooltipActive,
	});

	const stopPropagation = (event) => {
		event.stopPropagation();
	};

	return (
		<Tooltip
			id={`multi-ride-assignment-${storeId}`}
			content={ASSIGNMENT_LABEL_TEXT.multiRideTooltip}
			innerRef={targetInnerRef}
			balloonRef={balloonInnerRef}
			size="content-fit"
			openOnHover
			position={smartPosition}
			alignment={smartAlignment}
			wrapAround="inline"
			onToggle={setIsTooltipActive}
		>
			<span
				className={styles.multiRideIndicator}
				onClick={stopPropagation}
				onMouseDown={stopPropagation}
			>
				<span>{ASSIGNMENT_LABEL_TEXT.multiRideLabel}</span>
				<Icon
					InlineIcon={IconSwitch}
					extraClasses={styles.multiRideIcon}
					size={3}
					aria-hidden="true"
				/>
			</span>
		</Tooltip>
	);
};

const formatMinutes = (totalMinutes) => {
	const d = moment.duration(totalMinutes, 'minutes');
	const h = Math.floor(d.asHours());
	const m = d.minutes();
	return `${h}h ${m}m`;
};

const calcRideTimes = (stations) => {
	const sorted = [...(stations || [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

	// Whole time: first non-null departure -> last non-null arrival
	const startDeparture = sorted.find((s) => s.departure)?.departure ?? null;
	const endArrival = [...sorted].reverse().find((s) => s.arrival)?.arrival ?? null;

	const wholeMinutes =
		startDeparture && endArrival
			? Math.max(0, moment(endArrival).diff(moment(startDeparture), 'minutes'))
			: 0;

	// Driving time: sum(prev.departure -> curr.arrival)
	let drivingMinutes = 0;

	for (let i = 1; i < sorted.length; i++) {
		const prevDep = sorted[i - 1]?.departure;
		const currArr = sorted[i]?.arrival;

		if (!prevDep || !currArr) continue;

		const leg = moment(currArr).diff(moment(prevDep), 'minutes');
		if (leg > 0) drivingMinutes += leg;
	}

	return {
		wholeMinutes,
		wholeTimeLabel: formatMinutes(wholeMinutes),
		drivingMinutes,
		drivingTimeLabel: formatMinutes(drivingMinutes),
		stationStopMinutes: Math.max(0, wholeMinutes - drivingMinutes),
		stationStopLabel: formatMinutes(Math.max(0, wholeMinutes - drivingMinutes)),
	};
};

const findRun = (workAssigment) => {
	if (!workAssigment.run) return null;

	return (
		rideDetailsStore.details?.runs?.find(
			(run) => run.id === workAssigment.runId || run.name === workAssigment.run
		) ?? {
			id: workAssigment.runId,
			name: workAssigment.run,
		}
	);
};

const findJob = (workAssigment) => {
	if (!workAssigment.job) return null;

	return (
		rideDetailsStore.details?.jobs?.find(
			(job) => job.code === workAssigment.job || job.id === workAssigment.job
		) ?? {
			code: workAssigment.job,
		}
	);
};

const buildFullAssignmentData = (workAssigment, stations, rideId) => {
	const segmentStations = stations.filter(Boolean);
	const fromStation =
		segmentStations[0] ?? rideDetailsStore.getStationById(workAssigment.fromStationId);
	const toStation =
		segmentStations[segmentStations.length - 1] ??
		rideDetailsStore.getStationById(workAssigment.toStationId);

	return {
		crewMember: workAssigment.crewMember ?? null,
		job: findJob(workAssigment),
		run: findRun(workAssigment),
		sequenceGroupId: workAssigment.sequenceGroupId,
		status: workAssigment.status ?? null,
		workAssignmentSequences: [
			{
				extraActivities: workAssigment.extraActivities ?? [],
				fromStation,
				fromTimestamp: workAssigment.departure ?? fromStation?.departure ?? null,
				line: { code: rideDetailsStore.details?.lineCode },
				ride: {
					departure: rideDetailsStore.details?.departure,
					id: rideId,
					lineCode: rideDetailsStore.details?.lineCode,
					stations: segmentStations,
					tripNumber: rideDetailsStore.details?.tripNumber,
				},
				sequenceGroupId: workAssigment.sequenceGroupId,
				status: workAssigment.status ?? null,
				toStation,
				toTimestamp: workAssigment.arrival ?? toStation?.arrival ?? null,
			},
		],
	};
};

const Assigment = observer(({ storeId, container, isBreakdownAdjacent = false }) => {
	const active = useActiveElements();
	const workAssigment = rideDetailsStore.getAssignmentByID(storeId);
	const triggerRefA = useRef(null);
	const triggerRefB = useRef(null);

	if (!workAssigment) return;

	const stations = getStationsForAssignment(
		workAssigment.fromStationId,
		workAssigment.toStationId,
		rideDetailsStore.stations
	).map((id) => rideDetailsStore.getStationById(id));

	const stats = calcRideTimes(stations);

	const isFirst = workAssigment.fromStationId === rideDetailsStore.stations[0].id;
	const isLast =
		workAssigment.toStationId ===
		rideDetailsStore.stations[rideDetailsStore.stations.length - 1].id;

	const driverName = workAssigment.crewMember?.fullName || undefined;
	const driverId = workAssigment.crewMember?.id || undefined;
	const { status, isLoading, isPreparedToBatch } = workAssigment;
	const rideId = workAssigment.rideId ?? rideDetailsStore.rideId;

	const isAssigned =
		!isLoading && workAssigment?.crewMember !== null && driverName !== undefined && status !== null;
	const canOpenOrphanedDetails =
		!isLoading &&
		!isAssigned &&
		Boolean(workAssigment.sequenceGroupId) &&
		workAssigment.crewMember !== null;

	const isEmpty = !isLoading && driverName === undefined && status === null;
	const isRejected = status === ASSIGNMENT_STATUSES.REJECTED;
	const isVoided = isRejected && rideDetailsStore.details?.breakdown != null;

	const runLabel = workAssigment.run === null ? 'NA' : workAssigment.run;
	const jobLabel = workAssigment.job === null ? 'NA' : workAssigment.job;
	const runAndJobLabel = `Run ${runLabel} / Job ${jobLabel}`;

	const handleRunNavigation = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (workAssigment.runId) {
			const planningUrl = getPlanningUrl(workAssigment.runId);
			window.open(planningUrl, '_blank');
		}
	};

	const segmentBoxStyles = classNames({
		[styles.assignmentBox]: true,
		[styles.empty]: isEmpty || !isAssigned,
		[styles.assigned]: isAssigned,
		[styles.loading]: isLoading,
		[styles.rejected]: isRejected,
		[styles.voided]: isVoided,
		[styles.confirmed]: status === ASSIGNMENT_STATUSES.CONFIRMED && !isAssigned,
		[styles.finished]: status === ASSIGNMENT_STATUSES.ACTUAL && !isAssigned,
		[styles.batchHighlight]: isPreparedToBatch,
		[styles.breakdownAdjacent]: isBreakdownAdjacent,
	});

	const runJobsStyle = classNames({
		[styles.runJobData]: true,
		[styles.stub]: workAssigment.run === null && workAssigment.job === null,
	});

	return (
		<>
			<div
				className={segmentBoxStyles}
				onClick={() => {
					if (isBreakdownAdjacent) return;
					if (canOpenOrphanedDetails) {
						active.setActiveElement(SIDEBAR_NAMES.waDetails, {
							currentRideId: rideId,
							fullAssignmentData: buildFullAssignmentData(workAssigment, stations, rideId),
						});
						return;
					}

					if (isEmpty || !isAssigned) {
						rideDetailsStore.initializeAssignmentState(storeId);
						return;
					}
					active.setActiveElement(SIDEBAR_NAMES.waDetails, {
						crewMemberId: driverId,
						sequenceGroupId: workAssigment.sequenceGroupId,
						currentRideId: rideId,
					});
				}}
			>
				{isLoading && (
					<GridSticky>
						<div className={`${styles.stickyRow} ${styles.loadingRow}`}>
							<Spinner size="md" />
						</div>
					</GridSticky>
				)}
				{isAssigned && (
					<GridSticky>
						<div className={styles.stickyRow}>
							<div className={styles.information}>
								<div className={runJobsStyle}>
									<span className={styles.runJobLabel}>{runAndJobLabel}</span>
									<Icon
										onClick={handleRunNavigation}
										extraClasses={`${styles.secondary} ${styles.goTo} ${!workAssigment.runId ? styles.disabled : ''}`}
										size={3}
										InlineIcon={IconNewTab}
									/>
									{workAssigment.isMultiRide && <MultiRideIndicator storeId={storeId} />}
								</div>

								<Button
									extraClasses={styles.driver}
									appearance="link"
									Elem="a"
									href={`/crew-management/${driverId}`}
									target="_blank"
								>
									{driverName}
								</Button>

								{status !== ASSIGNMENT_STATUSES.ACTUAL && !isBreakdownAdjacent && (
									<div ref={triggerRefA} className={styles.options}>
										<AssignmentActionMenu
											triggerRef={triggerRefA}
											id={storeId}
											status={status}
											container={container}
											isFirst={isFirst}
											isLast={isLast}
											isTag={true}
										>
											<AssignmentStatusTag status={status} isVoided={isVoided} />
										</AssignmentActionMenu>
									</div>
								)}
								{(status === ASSIGNMENT_STATUSES.ACTUAL || isBreakdownAdjacent) && (
									<AssignmentStatusTag status={status} isVoided={isVoided} />
								)}
							</div>
							{status !== ASSIGNMENT_STATUSES.ACTUAL && !isBreakdownAdjacent && (
								<div ref={triggerRefB} className={styles.options}>
									<AssignmentActionMenu
										triggerRef={triggerRefB}
										id={storeId}
										status={status}
										container={container}
										isFirst={isFirst}
										isLast={isLast}
										isTag={false}
									>
										<Button appearance="link" display="square" aria-label="Action menu">
											<Icon InlineIcon={IconKebab} aria-hidden="true" />
										</Button>
									</AssignmentActionMenu>
								</div>
							)}
							{status !== ASSIGNMENT_STATUSES.ACTUAL && isBreakdownAdjacent && (
								<div className={styles.options} aria-disabled="true">
									<Button appearance="link" display="square" aria-label="Action menu" disabled>
										<Icon InlineIcon={IconKebab} aria-hidden="true" />
									</Button>
								</div>
							)}
						</div>
					</GridSticky>
				)}
				{(isEmpty || !isAssigned) && (
					<GridSticky>
						<div className={styles.empty}>
							<div className={runJobsStyle}>
								<span className={styles.runJobLabel}>{runAndJobLabel}</span>
								<Icon
									onClick={handleRunNavigation}
									extraClasses={`${styles.secondary} ${styles.goTo} ${!workAssigment.runId ? styles.disabled : ''}`}
									size={3}
									InlineIcon={IconNewTab}
								/>
								{workAssigment.isMultiRide && <MultiRideIndicator storeId={storeId} />}
							</div>
							<span className={`${styles.noDriver} ${styles.secondary}`}>
								{ASSIGNMENT_LABEL_TEXT.noAssignment}
							</span>
							<span className={styles.cta}>{ASSIGNMENT_LABEL_TEXT.assignLabel}</span>
						</div>
					</GridSticky>
				)}
			</div>
			<AssigmentDuration timeStat={stats} status={status} isVoided={isVoided} />
		</>
	);
});

export default Assigment;
